import React, { forwardRef } from "react";

import {
  mdiAlert,
  mdiAlertCircle,
  mdiCheckCircle,
  mdiInformation,
} from "@mdi/js";
import Icon from "@mdi/react";
import classNames from "classnames";

import type { JSX } from "react";

type NotifSeverity = "error" | "info" | "success" | "warning";

interface NotifMessageProps {
  details?: string[];
  message: string;
  severity?: NotifSeverity;
}

const severityClassNames: Record<NotifSeverity, string> = {
  error: "alert-danger",
  info: "alert-info",
  success: "alert-success",
  warning: "alert-warning",
};

function NotifMessage(
  { details, message, severity = "error" }: NotifMessageProps,
  ref: React.ForwardedRef<HTMLDivElement>,
): JSX.Element {
  const componentClass = severityClassNames[severity];

  const alertClassNames = classNames(
    "p-3.5 mb-5 border border-solid rounded-xs flex gap-2 items-center",
    componentClass,
    {
      "bg-destructive/10 border-status-error text-destructive":
        severity === "error",
      "bg-status-info/15 border-status-info text-status-info":
        severity === "info",
      "bg-status-active/15 border-status-active text-status-active":
        severity === "success",
      "bg-status-pause/15 border-status-pause text-status-pause":
        severity === "warning",
    },
  );

  const getIcon = () => {
    switch (severity) {
      case "error":
        return mdiAlertCircle;
      case "info":
        return mdiInformation;
      case "success":
        return mdiCheckCircle;
      case "warning":
        return mdiAlert;
      default:
        return mdiAlertCircle;
    }
  };

  return (
    <div ref={ref} className="form-group">
      <div className={alertClassNames}>
        <Icon path={getIcon()} size={0.8} />
        <span> {`  ${message}`} </span>
        {details && details.length > 0 ? (
          <ul>
            {details.map((detail) => (
              <li key={detail}>{detail}</li>
            ))}
          </ul>
        ) : null}
      </div>
    </div>
  );
}

export default forwardRef(NotifMessage);
